import { Download } from 'lucide-react';
import { useRef, useState } from 'react';
import { api, useSignAct, useUploadActScan, useWorkspace } from '@/api';
import type { Act } from '@/api/types';
import { formatDate, formatMoney } from '@/lib/format';
import { DownloadButton } from './DownloadButton';
import { Dialog } from './ui';

/**
 * Signing an act is the same simple e-signature as signing a document: the owner of the cabinet reads
 * the act, types their name and confirms; the studio keeps the time, the name and the fingerprint of the PDF.
 */
export function ActSignDialog({ act, onClose }: { act: Act; onClose: () => void }) {
  const { data: ws } = useWorkspace();
  const sign = useSignAct();
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState('');
  const period = act.periodFrom || act.periodTo ? [act.periodFrom, act.periodTo].filter(Boolean).map((d) => formatDate(d!)).join(' — ') : '';
  return (
    <Dialog open onClose={onClose} title={`Подписать акт № ${act.number}`}>
      <div className="space-y-4 p-5">
        <div className="rounded bg-panel p-3 text-sm shadow-hair">
          <div className="font-medium">
            Акт № {act.number} от {formatDate(act.issuedAt)}
          </div>
          <div className="mt-0.5 text-muted">
            на сумму <span className="num">{formatMoney(act.total)}</span>
            {period ? `, период ${period}` : ''}
          </div>
          {act.documentId ? (
            <DownloadButton name={act.fileName} load={() => api.actFile(act.id)} className="btn-secondary mt-2" label={`Открыть ${act.fileName}`}>
              <Download className="size-4" /> Открыть PDF
            </DownloadButton>
          ) : (
            <p className="mt-1 text-overdue">Файла акта нет — попросите студию прислать его</p>
          )}
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>Я прочитал акт, работы приняты, и я подписываю его простой электронной подписью по договору со студией.</span>
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Имя и фамилия, как в договоре</span>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder={ws?.me.name} />
        </label>
        <ScanUpload act={act} onDone={onClose} />
        {sign.error && <p className="text-sm text-overdue">{(sign.error as Error).message}</p>}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn-primary"
            disabled={!act.documentId || !agree || !name.trim() || sign.isPending}
            onClick={() => sign.mutate({ id: act.id, name: name.trim(), consent: true }, { onSuccess: onClose })}
          >
            Подписать
          </button>
        </div>
        <p className="text-xs text-muted">Сохраним время, ваше имя и отпечаток файла акта.</p>
      </div>
    </Dialog>
  );
}

/** Instead of clicking «Подписать»: a scan of the act signed on paper. It counts as the signature. */
function ScanUpload({ act, onDone }: { act: Act; onDone: () => void }) {
  const upload = useUploadActScan();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="rounded p-3 text-sm shadow-hair">
      <p className="text-muted">Подписали на бумаге? Загрузите скан (PDF или фото): он засчитается как подпись.</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input ref={input} type="file" aria-label="Скан подписанного акта" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="text-sm" />
        <button
          type="button"
          className="btn-secondary h-8"
          disabled={upload.isPending}
          onClick={() => {
            const file = input.current?.files?.[0];
            if (file) upload.mutate({ id: act.id, file }, { onSuccess: onDone });
          }}
        >
          Загрузить скан
        </button>
      </div>
      {upload.error && (
        <p role="alert" className="mt-2 text-overdue">
          {(upload.error as Error).message}
        </p>
      )}
    </div>
  );
}

/** Where a non-owner sees the sign button: acts are signed by the owner of the cabinet only. */
export const ownerSignsAct = 'Подпишет владелец кабинета';
